// Account: the Carveny Mileage subscription, what's online, Sign Out, and Delete Online Account.

import { useState } from "react";
import { Icon } from "../components/Icon.tsx";
import { Modal } from "../components/Modal.tsx";
import { useAccountData } from "../data/AccountData.tsx";
import { formatLongDate, formatRelativeMoment, plural } from "../domain/format.ts";
import { signOut } from "../lib/session.ts";
import { supabase } from "../lib/supabase.ts";

export function Account() {
  const data = useAccountData();
  const [isConfirming, setIsConfirming] = useState(false);
  const { status } = data;
  const active = status.canUpload && status.subscribedUntil;

  const online = [
    plural(data.trips.length, "trip"),
    plural(data.vehicles.length, "vehicle"),
    plural(data.customCategoryCount, "custom category", "custom categories"),
    plural(data.places.length, "saved place"),
  ].join(" · ");

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <div className="cv-head"><h1 className="cv-title">Account</h1></div>

      <div className="cv-stack" style={{ gap: 16 }}>
        <section className="cv-card" aria-labelledby="sub-title">
          <div className="cv-set-row" style={{ padding: "20px 22px", alignItems: "flex-start" }}>
            <span className="cv-badge" style={{ "--tint": "var(--pine-fill)" } as React.CSSProperties}><Icon name="road" /></span>
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h2 id="sub-title" className="cv-section-title" style={{ fontSize: 18 }}>Carveny Mileage</h2>
                {active
                  ? <span className="cv-pill is-ok"><Icon name="check" size="sm" />Active</span>
                  : <span className="cv-pill is-warn">{status.subscribedUntil ? "Ended" : "Not subscribed"}</span>}
              </div>
              <p className="cv-hint" style={{ marginTop: 4, fontSize: 14 }}>
                {active && status.subscribedUntil
                  ? <>Active through <b style={{ color: "var(--ink)", fontWeight: 600 }}>{formatLongDate(status.subscribedUntil)}</b>. Carveny Online comes with it: backup, this website and restoring to a new iPhone.</>
                  : status.subscribedUntil
                    ? <>Ended {formatLongDate(status.subscribedUntil)}.{status.dataDeletedAfter && <> Your trips stay online until <b style={{ color: "var(--ink)", fontWeight: 600 }}>{formatLongDate(status.dataDeletedAfter)}</b>, ready to restore in the app.</>}</>
                    : "Subscribe in Carveny on your iPhone to back up trips and use this website."}
              </p>
            </div>
            <a className="cv-btn cv-btn-secondary" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">
              Manage in the App Store<Icon name="external" size="sm" />
            </a>
          </div>
        </section>

        <section className="cv-card" aria-labelledby="data-title">
          <div className="cv-card-pad" style={{ paddingBottom: 6 }}><h2 id="data-title" className="cv-section-title" style={{ fontSize: 18 }}>Your data online</h2></div>
          <div className="cv-rows">
            <div className="cv-set-row"><div><div className="t">Signed in with Apple</div><div className="d">{data.email ?? "Email hidden"}</div></div></div>
            <div className="cv-set-row"><div><div className="t">Online copy</div><div className="d num">{online}</div></div></div>
            <div className="cv-set-row">
              <div><div className="t">Last upload from your iPhone</div><div className="d">{data.lastUpload ? capitalized(formatRelativeMoment(data.lastUpload)) : "Nothing uploaded yet"}</div></div>
            </div>
          </div>
          <div style={{ padding: "4px 22px 20px" }}>
            <div className="cv-notice is-info">
              <Icon name="info" />
              <p>Only trips and what they need are online: vehicle names, custom categories, mileage rates and saved places. Fuel and service logs, photos and receipts stay in iCloud.</p>
            </div>
          </div>
        </section>

        <section className="cv-card cv-set-row" aria-labelledby="signout-title" style={{ padding: "18px 22px" }}>
          <div><h2 id="signout-title" className="t" style={{ fontSize: 15 }}>Sign out of this browser</h2><div className="d">Your trips stay online and keep backing up from your iPhone.</div></div>
          <div className="end">
            <button className="cv-btn cv-btn-secondary" type="button" onClick={() => void signOut("signed-out")}><Icon name="signOut" />Sign Out</button>
          </div>
        </section>

        <section className="cv-card cv-set-row" aria-labelledby="del-title" style={{ padding: "18px 22px", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 360px" }}>
            <h2 id="del-title" className="t" style={{ fontSize: 15, color: "var(--danger)" }}>Delete Online Account</h2>
            <div className="d" style={{ fontSize: 14 }}>Deletes your trips and everything else in Carveny Online, and stops Sign in with Apple for Carveny. Trips on your iPhone and in iCloud aren’t touched.</div>
          </div>
          <div className="end">
            <button className="cv-btn cv-btn-danger-quiet" type="button" onClick={() => setIsConfirming(true)}><Icon name="trash" />Delete Online Account…</button>
          </div>
        </section>
      </div>

      {isConfirming && <DeleteConfirm email={data.email} tripCount={data.trips.length} vehicleCount={data.vehicles.length} onCancel={() => setIsConfirming(false)} />}
    </div>
  );
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function DeleteConfirm({ email, tripCount, vehicleCount, onCancel }: { email: string | null; tripCount: number; vehicleCount: number; onCancel: () => void }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setIsDeleting(true);
    setError(null);
    const { data, error } = await supabase.functions.invoke<{ deleted: boolean; appleRevoked: boolean }>("delete-account", { method: "POST" });
    if (error || !data?.deleted) {
      setIsDeleting(false);
      setError("Your online account couldn’t be deleted. Check your connection and try again.");
      return;
    }
    await signOut(data.appleRevoked ? "deleted" : "deleted-revoke-failed");
  }

  return (
    <Modal titleId="del-h" describedBy="del-d" onClose={isDeleting ? () => {} : onCancel}>
      <h2 id="del-h">Delete your online account?</h2>
      <p id="del-d" className="cv-hint" style={{ marginTop: 8, fontSize: 14.5 }}>This removes Carveny Online{email ? ` for ${email}` : ""}. It can’t be undone.</p>
      <ul className="cv-list-plain" style={{ marginTop: 18, gap: 12 }}>
        <li><span style={{ color: "var(--danger)" }}><Icon name="trash" /></span><span>Deletes {plural(tripCount, "trip")}, {plural(vehicleCount, "vehicle")}, your custom categories, mileage rates and saved places from Carveny Online.</span></li>
        <li><span style={{ color: "var(--pine)" }}><Icon name="phone" /></span><span>Trips on your iPhone and in iCloud stay exactly as they are.</span></li>
        <li><span style={{ color: "var(--text-2)" }}><Icon name="cloudOff" /></span><span>Backup stops. Turn on Carveny Online in the app to start a new online copy.</span></li>
        <li><span style={{ color: "var(--text-2)" }}><Icon name="lock" /></span><span>Sign in with Apple stops for Carveny.</span></li>
      </ul>
      <div className="cv-notice is-info" style={{ marginTop: 18 }}>
        <Icon name="info" />
        <p>Your Carveny Mileage subscription keeps running. Cancel it in the App Store if you no longer need it.</p>
      </div>
      {error && <p role="alert" style={{ color: "var(--danger)", marginTop: 14 }}>{error}</p>}
      <div className="cv-modal-actions">
        <button className="cv-btn cv-btn-secondary" type="button" onClick={onCancel} disabled={isDeleting} autoFocus>Cancel</button>
        <button className="cv-btn cv-btn-danger" type="button" onClick={() => void confirm()} disabled={isDeleting}>
          {isDeleting ? "Deleting…" : "Delete Online Account"}
        </button>
      </div>
    </Modal>
  );
}
